/**
 * A contrast floor for tenant branding.
 *
 * `isBrandableColor` decides whether a value is a COLOUR. It has nothing to say about whether the
 * resulting screen can be read, and until now nothing did — so a tenant could set `text-primary` to
 * the same value as `surface-base` and the consent wording and the Decline affordance would vanish
 * while the Finish button, painted in `accent`, stayed perfectly legible. On the one screen this
 * product publishes a WCAG 2.2 AA claim about, and to a signer who has no relationship with the
 * tenant and no way to fix it.
 *
 * That is the failure this module exists to prevent, and note its shape: it is not "the tenant
 * chose ugly colours". It is a combination in which the thing a signer must be able to REFUSE with
 * disappears and the thing they can agree with does not. Nobody has to intend it for it to happen —
 * one wrong paste into a colour field is enough.
 *
 * ## Pairs, not colours
 *
 * Contrast is a property of a PAIR, and a tenant overrides only some tokens, so every check runs
 * against the palette that actually results: the product defaults with the tenant's overrides
 * applied on top. Checking an override on its own would miss the interesting case entirely —
 * `text-primary` is fine in isolation and invisible against a `surface-base` somebody else set.
 *
 * ## Thresholds
 *
 * WCAG 2.2: 4.5:1 for normal text (1.4.3), 3:1 for user-interface components and their boundaries
 * (1.4.11). Applied at those numbers rather than something stricter, because this is a floor that
 * REFUSES a customer's brand colour — it should reject exactly what is unreadable and nothing more.
 */

/** sRGB channels, 0-255. */
export type Rgb = { readonly r: number; readonly g: number; readonly b: number }

const HEX3 = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i
const HEX6 = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i
const FN = /^(rgba?|hsla?)\(\s*([^)]*)\)$/i

const clamp = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, n))

/** hsl -> rgb, the standard conversion. h in degrees, s and l as 0-1. */
function hslToRgb(h: number, s: number, l: number): Rgb {
  const c = (1 - Math.abs(2 * l - 1)) * s
  const hp = (((h % 360) + 360) % 360) / 60
  const x = c * (1 - Math.abs((hp % 2) - 1))
  const [r1, g1, b1] =
    hp < 1
      ? [c, x, 0]
      : hp < 2
        ? [x, c, 0]
        : hp < 3
          ? [0, c, x]
          : hp < 4
            ? [0, x, c]
            : hp < 5
              ? [x, 0, c]
              : [c, 0, x]
  const m = l - c / 2
  return {
    r: Math.round(((r1 ?? 0) + m) * 255),
    g: Math.round(((g1 ?? 0) + m) * 255),
    b: Math.round(((b1 ?? 0) + m) * 255),
  }
}

/**
 * A colour this module can reason about, or null.
 *
 * Null is not a failure — it means "cannot be evaluated here", and the caller decides. `oklch()`
 * and `oklab()` land there deliberately: converting them needs the OKLab matrix and a gamut map,
 * which is a colour library, and a WRONG conversion would either reject a legal brand colour or
 * pass an illegible one. Being honest about not knowing is better than either.
 */
export function parseColor(value: string): Rgb | null {
  const v = value.trim().toLowerCase()

  const short = HEX3.exec(v)
  if (short) {
    const [, r, g, b] = short
    return {
      r: parseInt(`${r}${r}`, 16),
      g: parseInt(`${g}${g}`, 16),
      b: parseInt(`${b}${b}`, 16),
    }
  }
  const long = HEX6.exec(v)
  if (long) {
    return {
      r: parseInt(long[1] as string, 16),
      g: parseInt(long[2] as string, 16),
      b: parseInt(long[3] as string, 16),
    }
  }

  const fn = FN.exec(v)
  if (fn === null) return null
  const kind = (fn[1] ?? '').replace('a', '')
  const args = (fn[2] ?? '').split(/[\s,/]+/).filter((p) => p.length > 0)
  if (args.length < 3) return null
  const nums = args.slice(0, 3).map((a) => {
    const pct = a.endsWith('%')
    const n = Number(pct ? a.slice(0, -1) : a)
    return { n, pct, ok: Number.isFinite(n) }
  })
  if (!nums.every((x) => x.ok)) return null
  const [a0, a1, a2] = nums as [(typeof nums)[0], (typeof nums)[0], (typeof nums)[0]]

  if (kind === 'rgb') {
    const chan = (x: (typeof nums)[0]) => clamp(Math.round(x.pct ? (x.n / 100) * 255 : x.n), 0, 255)
    return { r: chan(a0), g: chan(a1), b: chan(a2) }
  }
  // hsl: hue in degrees, saturation and lightness as percentages.
  return hslToRgb(a0.n, clamp(a1.n / 100, 0, 1), clamp(a2.n / 100, 0, 1))
}

/** WCAG relative luminance. */
export function relativeLuminance({ r, g, b }: Rgb): number {
  const channel = (c: number) => {
    const s = c / 255
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4)
  }
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b)
}

/** WCAG contrast ratio, 1 (identical) to 21 (black on white). */
export function contrastRatio(a: Rgb, b: Rgb): number {
  const la = relativeLuminance(a)
  const lb = relativeLuminance(b)
  const [hi, lo] = la >= lb ? [la, lb] : [lb, la]
  return (hi + 0.05) / (lo + 0.05)
}

/**
 * The product's own semantic palette, from packages/ui/src/styles/tokens.css.
 *
 * Held to that file by `packages/core/test/branding-contrast.test.ts`, because a default that
 * drifted would make this check evaluate a palette nobody renders — passing a combination that is
 * actually unreadable, or refusing one that is fine.
 */
export const PRODUCT_PALETTE: Readonly<Record<string, string>> = {
  'surface-base': '#fbfbfa',
  'surface-raised': '#ffffff',
  'surface-sunken': '#f4f4f2',
  'border-subtle': '#e6e6e3',
  'border-strong': '#d2d2ce',
  'text-primary': '#1c1c1a',
  'text-muted': '#63635e',
  'text-on-accent': '#ffffff',
  accent: '#3341cc',
  'accent-hover': '#262e9e',
  'focus-ring': '#3341cc',
  success: '#1a6b3c',
  warning: '#8a5a00',
  danger: '#a3251c',
}

type Rule = {
  readonly foreground: string
  readonly background: string
  readonly minimum: number
  /** What a signer loses if this pair fails. Reported to the tenant, so it names the consequence. */
  readonly consequence: string
}

/**
 * The pairs that decide whether a ceremony can be read and refused.
 *
 * Not every possible pair — a floor that refused half the colour space would be worked around
 * rather than obeyed. These are the ones where failure removes something a signer needs.
 */
export const CONTRAST_RULES: readonly Rule[] = [
  {
    foreground: 'text-primary',
    background: 'surface-base',
    minimum: 4.5,
    consequence: 'the consent wording and the Decline option would be unreadable',
  },
  {
    foreground: 'text-primary',
    background: 'surface-raised',
    minimum: 4.5,
    consequence: 'the document card text would be unreadable',
  },
  {
    foreground: 'text-muted',
    background: 'surface-base',
    minimum: 4.5,
    consequence: 'the explanatory text under each step would be unreadable',
  },
  {
    foreground: 'text-on-accent',
    background: 'accent',
    minimum: 4.5,
    consequence: 'the label on the Finish button would be unreadable',
  },
  {
    foreground: 'accent',
    background: 'surface-base',
    minimum: 3,
    consequence: 'the Finish button would not be distinguishable from the page',
  },
  {
    foreground: 'danger',
    background: 'surface-base',
    minimum: 3,
    consequence: 'a decline or error message would not stand out from the page',
  },
]

/**
 * The readable label colour for a given accent: whichever of ink or white contrasts better.
 *
 * The accent belongs to the TENANT; `text-on-accent` belongs to us. So when their brand colour and
 * our default white do not contrast, the thing to change is ours. Refusing the brand instead would
 * be the product telling a customer their logo colour is invalid, which is both wrong and the
 * fastest way to have this rule switched off.
 *
 * This is not hypothetical tidiness. The colour in production today is `#059669`, and white on it
 * is 3.77:1 — below the 4.5:1 WCAG 2.2 AA needs for the label on the Finish button. Ink on the same
 * green is 5.57:1. The button has been shipping with an unreadable label by that standard, and the
 * fix costs the tenant nothing.
 */
export function readableOnAccent(accent: string): string | null {
  const parsed = parseColor(accent)
  if (parsed === null) return null
  const ink = PRODUCT_PALETTE['text-primary'] as string
  const white = PRODUCT_PALETTE['text-on-accent'] as string
  const inkRatio = contrastRatio(parsed, parseColor(ink) as Rgb)
  const whiteRatio = contrastRatio(parsed, parseColor(white) as Rgb)
  return whiteRatio >= inkRatio ? white : ink
}

/**
 * The palette a tenant's overrides actually produce, derivations included.
 *
 * A tenant who sets one colour — which is the ordinary case, because `primary_color` is a single
 * column — should not have to reason about the four other tokens that colour interacts with. What
 * they did not choose, we choose for them, and only then is the result checked. Anything they DID
 * choose is left exactly as given: a derivation that silently overrode an explicit choice would be
 * a different bug.
 */
export function resolveTenantPalette(
  overrides: Readonly<Record<string, string>>,
): Record<string, string> {
  const resolved: Record<string, string> = { ...overrides }
  const accent = overrides.accent
  if (accent !== undefined && overrides['text-on-accent'] === undefined) {
    const readable = readableOnAccent(accent)
    if (readable !== null) resolved['text-on-accent'] = readable
  }
  return resolved
}

export type ContrastViolation = {
  readonly foreground: string
  readonly background: string
  /** Null when a colour could not be evaluated — see `parseColor`. */
  readonly ratio: number | null
  readonly minimum: number
  readonly message: string
}

/**
 * Check a tenant's overrides against the palette they would actually produce.
 *
 * `overrides` is what the tenant is setting; everything else comes from the product defaults. Only
 * rules touching at least one overridden token are evaluated — the product's own palette is not the
 * tenant's problem, and reporting a violation they cannot cause would be noise.
 *
 * A colour this module cannot evaluate is reported ONLY when the tenant set it on a rule that
 * matters. That is the honest position: we are refusing to publish a legibility claim we cannot
 * check, not asserting the colour is bad.
 */
export function checkContrast(
  overrides: Readonly<Record<string, string>>,
): readonly ContrastViolation[] {
  // Checked against what the tenant's choice RESOLVES to, not the raw overrides: `text-on-accent`
  // follows the accent unless they set it themselves, so judging the raw input would report a
  // violation the rendered screen does not have.
  const resolved = resolveTenantPalette(overrides)
  const palette: Record<string, string> = { ...PRODUCT_PALETTE, ...resolved }
  const violations: ContrastViolation[] = []

  for (const rule of CONTRAST_RULES) {
    const touched =
      Object.prototype.hasOwnProperty.call(resolved, rule.foreground) ||
      Object.prototype.hasOwnProperty.call(resolved, rule.background)
    if (!touched) continue

    const fg = parseColor(palette[rule.foreground] ?? '')
    const bg = parseColor(palette[rule.background] ?? '')
    if (fg === null || bg === null) {
      const unreadable =
        fg === null ? (palette[rule.foreground] ?? '') : (palette[rule.background] ?? '')
      violations.push({
        foreground: rule.foreground,
        background: rule.background,
        ratio: null,
        minimum: rule.minimum,
        message:
          `${rule.foreground} on ${rule.background}: ${unreadable} cannot be checked for contrast, ` +
          `so it cannot be accepted here — ${rule.consequence}. Use a hex, rgb() or hsl() colour.`,
      })
      continue
    }

    const ratio = contrastRatio(fg, bg)
    if (ratio < rule.minimum) {
      violations.push({
        foreground: rule.foreground,
        background: rule.background,
        ratio: Math.round(ratio * 100) / 100,
        minimum: rule.minimum,
        message:
          `${rule.foreground} on ${rule.background} is ${ratio.toFixed(2)}:1, below the ` +
          `${rule.minimum}:1 this screen needs — ${rule.consequence}.`,
      })
    }
  }

  return violations
}
